import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import InputBase from '@mui/material/InputBase';
import { useUsersQuery } from '../../../services/api';
import { useOptionList, useStages } from '../../../features/options/useOptions';
import { CHMAP } from '../../../constants/channels';
import { computeAge, todayISO } from '../../../utils/format';
import AppButton from '../../../components/common/AppButton';
import { useToast } from '../../../components/common/Toast';
import { TextField, SelectField, FieldLabel, CONTROL_SX } from '../../../components/common/FormField';
import { ConditionChip } from '../../../components/common/Bits';

const QUICK_CONDITIONS = ['BP', 'Sugar', 'Thyroid', 'Cholesterol', 'Asthma', 'Cardiac', 'PCOD'];

const blank = (source, owner, stage) => ({
  title: 'Mr.', firstName: '', lastName: '', dob: '', gender: 'Male',
  name: '', contact: '', phone: '', value: '', source, owner, stage, notes: '',
  addr1: '', addr2: '', city: '', mandal: '', district: '', state: '', pincode: '', conditions: [],
});

function FormSection({ children }) {
  return (
    <Box
      sx={{
        fontFamily: 'var(--mono)',
        fontSize: 10,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: 'var(--tealdim)',
        m: '4px 0 10px',
        pt: 0.75,
        borderTop: '1px solid var(--grid)',
      }}
    >
      {children}
    </Box>
  );
}

export default function LeadModal({ open, channelId, lead, saving = false, onClose, onSave }) {
  const toast = useToast();
  // owners are teammates, so the pick-list is the live active team, not a local copy
  const { data: team } = useUsersQuery({ active: 'true', limit: 200 });
  const users = team?.items || [];
  const titles = useOptionList('titles');
  const genders = useOptionList('genders');
  const sources = useOptionList('sources');
  const stages = useStages();

  const isD2C = channelId === 'd2c';
  const fresh = () => blank(sources[0], users[0]?.name || '', stages[0]?.k || 'New');
  const [form, setForm] = useState(fresh);
  const [conditions, setConditions] = useState([]);
  const [condInput, setCondInput] = useState('');

  useEffect(() => {
    if (!open) return;
    const base = lead ? { ...fresh(), ...lead } : fresh();
    setForm(base);
    setConditions(base.conditions || []);
    setCondInput('');
  }, [open, lead]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addCondition = (v) => {
    const t = (v || '').trim();
    if (t && !conditions.some((c) => c.toLowerCase() === t.toLowerCase())) setConditions((c) => [...c, t]);
    setCondInput('');
  };

  const submit = () => {
    const t = (k) => String(form[k] ?? '').trim();
    const data = {
      phone: t('phone'),
      value: +form.value || 0,
      source: form.source,
      owner: form.owner,
      stage: form.stage,
      notes: t('notes'),
      addr1: t('addr1'), addr2: t('addr2'),
      city: t('city'), mandal: t('mandal'),
      district: t('district'), state: t('state'),
    };

    if (isD2C) {
      const first = t('firstName');
      if (!first) return toast('First name is required', 'error');
      Object.assign(data, {
        title: form.title,
        firstName: first,
        lastName: t('lastName'),
        dob: form.dob || '',
        gender: form.gender,
        contact: '',
        name: [form.title, first, t('lastName')].filter(Boolean).join(' '),
        conditions: [...conditions],
      });
    } else {
      const name = t('name');
      if (!name) return toast('Lead / Organization name is required', 'error');
      data.name = name;
      data.contact = t('contact');
    }

    const pincode = t('pincode');
    if (!pincode) return toast('Pincode is required', 'error');
    if (!/^\d{6}$/.test(pincode)) return toast('Pincode must be a 6-digit number', 'error');
    data.pincode = pincode;

    return onSave(data);
  };

  const age = form.dob ? computeAge(form.dob) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: '16px', border: '1px solid var(--border)', background: 'var(--card)' } } }}
    >
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          p: '18px 22px', borderBottom: '1px solid var(--border)',
        }}
      >
        <Box sx={{ fontFamily: 'var(--disp)', fontSize: 18, fontWeight: 700 }}>
          {lead ? 'Edit lead' : 'Add lead'} · {CHMAP[channelId].name}
        </Box>
        <AppButton mini onClick={onClose} sx={{ width: 34, height: 34 }}>✕</AppButton>
      </Box>

      <Box sx={{ p: '20px 22px', maxHeight: '70vh', overflowY: 'auto' }}>
        {/* identity block — 12-column, differs for D2C patients vs organizations */}
        {isD2C ? (
          <>
            <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <SelectField label="Title" value={form.title} onChange={set('title')} options={titles} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4.5, md: 5 }}>
                <TextField label="First name" required value={form.firstName} onChange={set('firstName')} placeholder="First name" />
              </Grid>
              <Grid size={{ xs: 12, sm: 4.5, md: 5 }}>
                <TextField label="Last name" value={form.lastName} onChange={set('lastName')} placeholder="Last name" />
              </Grid>
            </Grid>
            <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField label="Date of birth" type="date" value={form.dob} onChange={set('dob')} inputProps={{ max: todayISO() }} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <TextField label="Age" readOnly value={age != null ? `${age} yrs` : ''} placeholder="—" />
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                <SelectField label="Gender" value={form.gender} onChange={set('gender')} options={genders} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField label="Phone number" value={form.phone} onChange={set('phone')} placeholder="e.g. 98480 12345" />
              </Grid>
            </Grid>
          </>
        ) : (
          <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField label="Lead / Organization" value={form.name} onChange={set('name')} placeholder="e.g. Apollo Clinic — OPD tie-up" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField label="Contact person" value={form.contact} onChange={set('contact')} placeholder="Name" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField label="Phone number" value={form.phone} onChange={set('phone')} placeholder="e.g. 98480 12345" />
            </Grid>
          </Grid>
        )}

        <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="Est. monthly value (₹)" type="number" inputProps={{ min: 0 }} value={form.value} onChange={set('value')} placeholder="0" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SelectField label="Source" value={form.source} onChange={set('source')} options={sources} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SelectField label="Owner" value={form.owner} onChange={set('owner')} options={users.map((u) => u.name)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SelectField label="Stage" value={form.stage} onChange={set('stage')} options={stages.map((s) => s.k)} />
          </Grid>
        </Grid>

        <FormSection>Address</FormSection>
        <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField label="Address line 1" value={form.addr1} onChange={set('addr1')} placeholder="House / building, street" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField label="Address line 2" value={form.addr2} onChange={set('addr2')} placeholder="Area / landmark" />
          </Grid>
        </Grid>
        <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <TextField label="City" value={form.city} onChange={set('city')} placeholder="City" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <TextField label="Mandal" value={form.mandal} onChange={set('mandal')} placeholder="Mandal" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <TextField label="District" value={form.district} onChange={set('district')} placeholder="District" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <TextField label="State" value={form.state} onChange={set('state')} placeholder="State" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <TextField
              label="Pincode" required value={form.pincode}
              onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value }))}
              inputProps={{ inputMode: 'numeric', maxLength: 6 }} placeholder="6-digit"
            />
          </Grid>
        </Grid>

        {isD2C && (
          <>
            <FormSection>Known conditions</FormSection>
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              {conditions.length ? (
                conditions.map((c, i) => (
                  <ConditionChip key={c} onRemove={() => setConditions((cs) => cs.filter((_, j) => j !== i))}>
                    {c}
                  </ConditionChip>
                ))
              ) : (
                <Box sx={{ color: 'var(--muted)', fontSize: 12 }}>No conditions added yet</Box>
              )}
            </Box>
            <Grid container spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InputBase
                  fullWidth
                  sx={CONTROL_SX}
                  placeholder="Type a condition & press Enter"
                  value={condInput}
                  onChange={(e) => setCondInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addCondition(condInput); }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <AppButton onClick={() => addCondition(condInput)}>＋ Add</AppButton>
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '7px', mt: 1.25 }}>
              {QUICK_CONDITIONS.map((c) => (
                <Box
                  key={c}
                  onClick={() => addCondition(c)}
                  sx={{
                    p: '5px 11px', border: '1px dashed var(--border)', borderRadius: '999px',
                    background: 'var(--card-2)', cursor: 'pointer', fontSize: 12, userSelect: 'none',
                    '&:hover': { borderColor: 'var(--mint)', background: '#eef8f2' },
                  }}
                >
                  ＋ {c}
                </Box>
              ))}
            </Box>
          </>
        )}

        <Box sx={{ mt: 2 }}>
          <FieldLabel>Notes</FieldLabel>
          <InputBase
            fullWidth multiline minRows={2}
            sx={{ ...CONTROL_SX, fontFamily: 'inherit' }}
            placeholder="Context, next step…"
            value={form.notes}
            onChange={set('notes')}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.25, justifyContent: 'flex-end', p: '16px 22px', borderTop: '1px solid var(--border)' }}>
        <AppButton onClick={onClose} disabled={saving}>Cancel</AppButton>
        <AppButton tone="primary" onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : 'Save lead'}
        </AppButton>
      </Box>
    </Dialog>
  );
}
